import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import GoogleCalendarSettings from "@/pages/account/Settings/GoogleCalendarSettings";
import { ArrowRight, ArrowLeft, Plus, Trash2 } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/layout/PageHeader";
import { FormInput, FormSwitch } from "@/components/forms/FormControls";
import PhoneField from "@/components/forms/PhoneField";
import PhoneVerification from "@/components/account/PhoneVerification";
import { DEFAULT_COUNTRY, fromE164, toE164 } from "@/lib/phone";
import type { CountryCode } from "libphonenumber-js/max";
import SectionCard from "@/components/common/SectionCard";
import EmptyState from "@/components/common/EmptyState";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import LoginHistorySkeleton from "@/components/account/skeletons/LoginHistorySkeleton";
import StatusTemplates from "@/pages/account/Settings/StatusTemplates";
import { brokerageProfileSchema, changePasswordSchema, myProfileSchema } from "@/schemas/settings";
import type { BrokerageProfileValues, ChangePasswordValues, MyProfileValues } from "@/schemas/settings";
import { cn, formatDateTime } from "@/utils/common.utils";
import { SETTINGS_TABS } from "@/constant/account.constant";
import { useAuth } from "@/lib/auth";
import {
  useChangePassword,
  useDeleteAccount,
  useGetDeleteCheck,
  useGetLoginHistory,
  useGetMyProfile,
  useGetOrgProfile,
  usePatchMyPhoto,
  usePatchMyProfile,
  usePatchOrgProfile,
  useSetPhoneLogin,
} from "@/hooks/useSettings";
import { usePostUploadImage } from "@/hooks/useFiles";
import UserAvatar from "@/components/common/UserAvatar";
import type { TaskDueReminderRule } from "@/types/settings.types";

const EMPTY_MY_PROFILE: MyProfileValues = {
  firstName: "", lastName: "", secondaryEmail: "", phone: "", phoneCountry: DEFAULT_COUNTRY,
  address: "", emailNotifications: true, smsNotifications: false,
};

const DEFAULT_REMINDER_RULES: TaskDueReminderRule[] = [
  { recipient: "agent", days_before: 7, enabled: true },
  { recipient: "agent", days_before: 3, enabled: true },
  { recipient: "client", days_before: 7, enabled: true },
  { recipient: "client", days_before: 3, enabled: true },
];

/** Uploads and saves on pick — no "Save changes" — so it lives outside the form. */
function ProfilePhoto() {
  const { user } = useAuth();
  const { mutateAsync: uploadImage, isPending: uploading } = usePostUploadImage();
  const { mutate: savePhoto, isPending: saving } = usePatchMyPhoto();
  const busy = uploading || saving;

  const pick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // so picking the same file again still fires
    if (!file) return;
    const res = await uploadImage(file).catch(() => null);
    if (res?.data?.url) savePhoto(res.data.url);
  };

  return (
    <div className="sm:col-span-2 mb-2 flex items-center gap-4">
      <UserAvatar name={user?.name} src={user?.photo_url} className="size-16" fallbackClassName="text-lg font-bold" />
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Button asChild variant="dashboardOutline" size="sm" disabled={busy}>
            <label className={cn("cursor-pointer", busy && "pointer-events-none opacity-50")} data-testid="settings-photo-upload">
              {busy ? "Uploading…" : user?.photo_url ? "Change photo" : "Upload photo"}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="sr-only" onChange={pick} disabled={busy} />
            </label>
          </Button>
          {user?.photo_url && (
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => savePhoto("")} data-testid="settings-photo-remove">
              Remove
            </Button>
          )}
        </div>
        <p className="text-xs text-dash-faint">JPG, PNG, WEBP or GIF, up to 8MB.</p>
      </div>
    </div>
  );
}

/** "Allow sign-in with this phone". Saves on toggle, like the photo, so it acts
 *  on its own rather than through the profile form. Turning it on asks for the
 *  password: it adds a way into the account. Spec: docs/phone-login.md. */
function PhoneLoginSetting({ enabled, stale, onReverify }: { enabled: boolean; stale: boolean; onReverify: () => void }) {
  const { mutateAsync, isPending } = useSetPhoneLogin();
  const [asking, setAsking] = useState(false);
  const [password, setPassword] = useState("");

  const turnOn = async () => {
    try {
      await mutateAsync({ enabled: true, current_password: password });
      setAsking(false);
    } catch {
      /* toasted by the hook */
    } finally {
      setPassword("");
    }
  };

  return (
    <div className="mt-3 flex items-start justify-between gap-4" data-testid="phone-login-setting">
      <div className="space-y-1">
        <span className="block text-sm font-medium text-dash-ink">Allow sign-in with this phone</span>
        <p className="text-xs text-dash-faint">
          {stale ? (
            <>
              Verified over a year ago.{" "}
              <button type="button" className="underline hover:text-dash-ink" onClick={onReverify} data-testid="phone-login-reverify">
                Verify again
              </button>{" "}
              to use it for sign-in.
            </>
          ) : (
            "Sign in with a code texted to this number instead of your password."
          )}
        </p>
      </div>
      <Switch
        checked={enabled}
        disabled={stale || isPending}
        onCheckedChange={(on) => (on ? setAsking(true) : void mutateAsync({ enabled: false }).catch(() => undefined))}
        aria-label="Allow sign-in with this phone"
        data-testid="phone-login-switch"
      />
      <ConfirmDialog
        open={asking}
        onOpenChange={(open) => { setAsking(open); if (!open) setPassword(""); }}
        tone="default"
        title="Turn on phone sign-in?"
        description="Anyone holding this phone will be able to sign in with a texted code. Enter your password to confirm."
        confirmLabel="Turn on"
        busyLabel="Turning on…"
        onConfirm={turnOn}
        testId="phone-login-confirm"
      >
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          placeholder="Current password"
          aria-label="Current password"
          autoFocus
          data-testid="phone-login-password"
        />
      </ConfirmDialog>
    </div>
  );
}

function MyProfile() {
  const { data: profile, isLoading } = useGetMyProfile();
  const { mutate: patchProfile, isPending } = usePatchMyProfile();
  const { refreshContext } = useAuth();
  // Reopens the send-code step for a number verified too long ago for sign-in.
  // Only on request: texts still reach that number, so it is not a warning state.
  const [reverify, setReverify] = useState(false);
  const reverifying = reverify && Boolean(profile?.phone_login_stale);

  const form = useForm<MyProfileValues>({
    resolver: zodResolver(myProfileSchema),
    defaultValues: EMPTY_MY_PROFILE,
  });

  // Seeded once the fetch lands, for the same reason the brokerage form is:
  // `reset` keeps the form pristine so the dirty check stays meaningful.
  useEffect(() => {
    if (!profile) return;
    form.reset({
      firstName: profile.first_name,
      lastName: profile.last_name,
      secondaryEmail: profile.secondary_email,
      // Split back into the pair the field edits. `PhoneField` would adopt a
      // bare E.164 on its own, but seeding it correctly here avoids a first
      // paint showing the default flag against another country's number.
      phone: fromE164(profile.phone)?.national ?? profile.phone,
      phoneCountry: fromE164(profile.phone)?.country ?? DEFAULT_COUNTRY,
      address: profile.address,
      emailNotifications: profile.email_notifications,
      // Shown off until the number is verified. The stored flag defaults on
      // (it means "stop texting me", not "start"), but a switch that reads as
      // on before anyone has opted in is a pre-checked consent box to a
      // carrier reviewer. Verifying is what turns it on — the server sets it.
      smsNotifications: profile.phone_verified && profile.sms_notifications,
    });
  }, [profile, form]);

  const save = (values: MyProfileValues) => {
    patchProfile(
      {
        first_name: values.firstName,
        last_name: values.lastName,
        secondary_email: values.secondaryEmail,
        // Resolved against the picked country before sending. The server parses
        // with a US default and never sees `phoneCountry` — it is form-local —
        // so a Pakistani mobile typed as "3120287142" would be read as a US
        // number and rejected. `toE164` makes the country explicit in the value
        // itself, which is the only thing that crosses the wire.
        // "" is preserved rather than coerced: clearing the number is a real edit.
        phone: values.phone
          ? toE164(values.phone, (values.phoneCountry || DEFAULT_COUNTRY) as CountryCode) ?? values.phone
          : "",
        address: values.address,
        email_notifications: values.emailNotifications,
        // Omitted until verified: the switch is locked off then, and sending
        // that `false` would record an opt-out nobody chose.
        ...(profile?.phone_verified ? { sms_notifications: values.smsNotifications } : {}),
      },
      // First and last name are mirrored onto the user's `name`, which context
      // feeds to the sidebar and avatar — refresh so the rename shows at once.
      { onSuccess: () => { refreshContext?.(); } },
    );
  };

  return (
    <Form {...form}>
      <SectionCard
        title="Your profile"
        action={
          <Button
            type="submit"
            form="my-profile-form"
            variant="dashboardPrimary"
            loading={isPending}
            disabled={isLoading}
            data-testid="settings-save-my-profile"
          >
            Save changes
          </Button>
        }
      >
        <form id="my-profile-form" onSubmit={form.handleSubmit(save)} noValidate>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ProfilePhoto />
            <FormInput name="firstName" label="First name" placeholder="Enter your first name" autoComplete="given-name" required />
            <FormInput name="lastName" label="Last name" placeholder="Enter your last name" autoComplete="family-name" required />

            {/* Read-only: this is the address you sign in with, and changing it
                belongs in its own re-authenticated flow, not a profile save. */}
            <div className="sm:col-span-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <span className="block text-sm font-medium text-dash-ink">Primary email</span>
                <div
                  className="flex h-10 items-center rounded-md bg-dash-hover px-3 text-sm text-dash-muted"
                  data-testid="settings-primary-email"
                >
                  {profile?.primary_email || "—"}
                </div>
                <p className="text-xs text-dash-faint">Your sign-in address. Contact support to change it.</p>
              </div>
              <FormInput
                name="secondaryEmail"
                label="Secondary email"
                type="email"
                placeholder="Optional"
                autoComplete="email"
                description="Copied on notification emails."
              />
            </div>

            <div>
              <PhoneField name="phone" countryName="phoneCountry" label="Mobile number" testId="settings-phone" />
              {profile?.sms_available && (
                <PhoneVerification
                  savedPhone={profile?.phone || ""}
                  savedPhoneDisplay={profile?.phone_display || ""}
                  verified={Boolean(profile?.phone_verified) && !reverifying}
                  reverify={reverifying}
                  // Compared in E.164, not as typed. The form holds the number
                  // in national format ("0312 0287142") while the server holds
                  // E.164 ("+923120287142"), so comparing the raw strings marks
                  // an untouched field as permanently unsaved and the verify
                  // step never becomes reachable.
                  //
                  // Still compared against the *saved value* rather than RHF's
                  // own dirty flag: editing the address must not block verifying
                  // a phone the server already has.
                  dirty={
                    (form.watch("phone")
                      ? toE164(form.watch("phone"), (form.watch("phoneCountry") || DEFAULT_COUNTRY) as CountryCode) ?? form.watch("phone")
                      : "") !== (profile?.phone || "")
                  }
                />
              )}
              {profile?.sms_available && profile.phone_verified && !reverifying && (
                <PhoneLoginSetting
                  enabled={profile.phone_login_enabled}
                  stale={profile.phone_login_stale}
                  onReverify={() => setReverify(true)}
                />
              )}
            </div>
            <FormInput name="address" label="Address" placeholder="Optional" autoComplete="street-address" />

            <div className="sm:col-span-2 mt-2 space-y-3 rounded-lg bg-dash-hover px-4 py-3">
              <FormSwitch
                name="emailNotifications"
                label="Email notifications"
                description="Reminders, trial notices and client messages. Sign-in and security emails are always sent."
              />
              {/* Hidden outright when Twilio is unconfigured — the same
                  precedent as the AI Studio tab and the invite form's SMS
                  toggle: never offer a control that can only fail. */}
              {profile?.sms_available && (
                <div className="border-t border-dash-border pt-3">
                  <FormSwitch
                    name="smsNotifications"
                    label="Text notifications"
                    disabled={!profile?.phone_verified}
                    description={
                      profile?.phone_verified
                        ? "Deadlines, overdue tasks and client messages, sent to your verified mobile."
                        : "Verify your mobile number above to start receiving these."
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </SectionCard>
    </Form>
  );
}

function RemindersSettings() {
  const { data: profile, isLoading } = useGetMyProfile();
  const { mutate: patchProfile, isPending } = usePatchMyProfile();
  const [rules, setRules] = useState<TaskDueReminderRule[]>(DEFAULT_REMINDER_RULES);

  useEffect(() => {
    if (!profile) return;
    setRules((profile.task_due_reminder_rules ?? DEFAULT_REMINDER_RULES).map((rule) => ({
      recipient: rule.recipient,
      days_before: rule.days_before,
      enabled: rule.enabled,
    })));
  }, [profile]);

  const updateRule = (index: number, patch: Partial<TaskDueReminderRule>) => {
    setRules((current) => current.map((rule, i) => (
      i === index ? { ...rule, ...patch } : rule
    )));
  };

  const addRule = () => {
    setRules((current) => [...current, { recipient: "agent", days_before: 3, enabled: true }]);
  };

  const removeRule = (index: number) => {
    setRules((current) => current.filter((_, i) => i !== index));
  };

  const save = () => {
    patchProfile({
      task_due_reminder_rules: rules.map((rule) => ({
        recipient: rule.recipient,
        days_before: Math.min(30, Math.max(0, Math.trunc(Number(rule.days_before) || 0))),
        enabled: rule.enabled,
      })),
    });
  };

  return (
    <SectionCard
      title="Task due date reminders"
      action={
        <Button
          type="button"
          variant="dashboardPrimary"
          loading={isPending}
          disabled={isLoading}
          onClick={save}
          data-testid="settings-save-reminders"
        >
          Save changes
        </Button>
      }
    >
      <div className="space-y-3">
        {rules.length === 0 ? (
          <EmptyState
            compact
            title="No reminders configured"
            description="Add a reminder to notify agents or clients before a task due date."
            className="border-0 bg-dash-hover"
          />
        ) : (
          rules.map((rule, index) => (
            <div
              key={`${rule.recipient}-${rule.days_before}-${index}`}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg bg-dash-hover px-3 py-3 sm:grid-cols-[auto_minmax(150px,1fr)_minmax(120px,160px)_auto]"
            >
              <Checkbox
                checked={rule.enabled}
                onCheckedChange={(checked) => updateRule(index, { enabled: checked === true })}
                aria-label={`Enable reminder ${index + 1}`}
              />
              <label className="min-w-0 text-sm font-medium text-dash-ink">
                Notify
                <select
                  value={rule.recipient}
                  onChange={(event) => updateRule(index, { recipient: event.target.value as TaskDueReminderRule["recipient"] })}
                  className="ml-2 h-9 rounded-[8px] border border-dash-border bg-white px-2 text-sm text-dash-ink"
                  aria-label={`Reminder recipient ${index + 1}`}
                >
                  <option value="agent">Agent</option>
                  <option value="client">Client</option>
                </select>
              </label>
              <label className="col-span-2 flex items-center gap-2 text-sm text-dash-muted sm:col-span-1">
                <Input
                  type="number"
                  min={0}
                  max={30}
                  step={1}
                  inputMode="numeric"
                  value={rule.days_before}
                  onChange={(event) => updateRule(index, { days_before: Number(event.target.value || 0) })}
                  className="w-20 rounded-[8px]"
                  aria-label={`Days before due date ${index + 1}`}
                />
                days before
              </label>
              <Button
                type="button"
                variant="dashboardOutline"
                size="icon"
                onClick={() => removeRule(index)}
                aria-label={`Remove reminder ${index + 1}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))
        )}
        <Button
          type="button"
          variant="dashboardOutline"
          onClick={addRule}
          className="mt-1"
          data-testid="settings-add-reminder"
        >
          <Plus className="size-4" /> Add reminder
        </Button>
      </div>
    </SectionCard>
  );
}

const EMPTY_PROFILE: BrokerageProfileValues = {
  legalName: "", displayName: "", supportEmail: "", phone: "", licenseNumber: "", address: "",
};

function BrokerageProfile({ orgId }: { orgId: string }) {
  const { data: profile, isLoading } = useGetOrgProfile(orgId);
  const { mutate: patchProfile, isPending } = usePatchOrgProfile();
  const { refreshContext } = useAuth();

  const form = useForm<BrokerageProfileValues>({
    resolver: zodResolver(brokerageProfileSchema),
    defaultValues: EMPTY_PROFILE,
  });

  // The form is created before the fetch resolves, so seed it once the real
  // values land. `reset` rather than per-field setValue keeps the form pristine,
  // so the dirty check still means "the user changed something".
  useEffect(() => {
    if (!profile) return;
    form.reset({
      legalName: profile.legal_name,
      displayName: profile.display_name,
      supportEmail: profile.support_email,
      phone: profile.phone,
      licenseNumber: profile.license_number,
      address: profile.address,
    });
  }, [profile, form]);

  const save = (values: BrokerageProfileValues) => {
    patchProfile(
      {
        orgId,
        payload: {
          legal_name: values.legalName,
          display_name: values.displayName,
          support_email: values.supportEmail,
          phone: values.phone,
          license_number: values.licenseNumber,
          address: values.address,
        },
      },
      // display_name is mirrored onto the organization's `name`, which the
      // sidebar reads from context — refresh so the rename shows immediately.
      { onSuccess: () => { refreshContext?.(); } },
    );
  };

  return (
    <Form {...form}>
      <SectionCard
        title="Brokerage profile"
        action={
          <Button
            type="submit"
            form="brokerage-profile-form"
            variant="dashboardPrimary"
            loading={isPending}
            disabled={isLoading}
            data-testid="settings-save-profile"
          >
            Save changes
          </Button>
        }
      >
        <form id="brokerage-profile-form" onSubmit={form.handleSubmit(save)} noValidate>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormInput name="legalName" label="Legal name" placeholder="Enter your legal name" autoComplete="organization" />
            <FormInput name="displayName" label="Display name" placeholder="Enter your display name" required />
            <FormInput name="supportEmail" label="Support email" type="email" placeholder="Enter your support email" autoComplete="email" />
            <FormInput name="phone" label="Phone" type="tel" placeholder="Enter your phone number" autoComplete="tel" />
            <FormInput name="licenseNumber" label="License number" placeholder="Enter your license number" />
            <div className="sm:col-span-2">
              <FormInput name="address" label="Address" placeholder="Enter your address" autoComplete="street-address" />
            </div>
          </div>
        </form>
      </SectionCard>
    </Form>
  );
}

function SecurityList({ onChangePassword }: { onChangePassword: () => void }) {
  const { data, isLoading } = useGetLoginHistory({ limit: 10 });
  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <SectionCard title="Change password">
        <Button variant="seamless"
          type="button"
          onClick={onChangePassword}
          className="flex w-full items-center justify-between rounded-lg bg-dash-hover px-4 py-3.5 text-left hover:bg-dash-chip"
          data-testid="settings-open-change-password"
        >
          <span>
            <span className="block text-sm font-medium text-dash-ink">Change Password</span>
            <span className="block text-xs text-dash-faint">Signs out your other devices</span>
          </span>
          <ArrowRight className="size-4 text-dash-muted" />
        </Button>
      </SectionCard>

      <SectionCard title="Login history">
        {isLoading ? (
          <LoginHistorySkeleton />
        ) : items.length === 0 ? (
          // Sessions before this was instrumented were never recorded, so a new
          // deployment legitimately starts empty rather than back-filled.
          <EmptyState
            compact
            title="No sign-ins recorded yet"
            description="Your sign-ins will be listed here from now on."
            className="border-0"
          />
        ) : (
          <div className="space-y-2">
            {items.map((row) => (
              <div key={row.id} className="flex flex-col gap-2 rounded-lg bg-dash-hover px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className={`size-2 shrink-0 rounded-full ${row.status === "ok" ? "bg-dash-greenDeep" : "bg-dash-rust"}`} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-dash-ink">{row.label}</span>
                    <span className="block truncate text-xs text-dash-faint">{row.user_agent} · {row.ip}</span>
                  </span>
                </div>
                <span className="shrink-0 pl-5 text-xs text-dash-faint sm:pl-0">{formatDateTime(row.at)}</span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <DeleteAccount />
    </div>
  );
}

const DELETE_REMOVES = [
  "Your name, email, phone number and photo",
  "Your login history and notifications",
  "Your access to every deal and studio you belong to",
  "Every signed-in session, on web and mobile",
];

const DELETE_KEEPS = [
  "Messages, documents and activity you shared stay on each deal, shown as “Deleted user”",
  "Other people on your deals keep their full history",
];

function DetailList({ heading, items }: { heading: string; items: ReactNode[] }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-dash-muted">{heading}</p>
      <ul className="list-disc space-y-1 pl-5 text-sm text-dash-ink">
        {items.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
    </div>
  );
}

function DeleteAccount() {
  const [open, setOpen] = useState(false);
  const { logout } = useAuth();
  const { mutateAsync: deleteAccount } = useDeleteAccount();
  const { data: check, isFetching: checking, refetch } = useGetDeleteCheck(open);
  const blockers = check?.blockers ?? [];

  const submit = async (confirmation: string) => {
    try {
      await deleteAccount(confirmation);
    } catch {
      return; // already toasted; keep the dialog open
    }
    logout();
  };

  return (
    <SectionCard title="Delete account">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-xs text-dash-faint">
          Permanently remove your account and personal details. Deals you were part of keep their history.
        </span>
        <Button type="button" variant="destructive" loading={open && checking} onClick={() => (open ? void refetch() : setOpen(true))} data-testid="settings-open-delete-account">
          <Trash2 className="size-4" /> Delete account
        </Button>
      </div>
      {open && check && !checking && (blockers.length > 0 ? (
        <ConfirmDialog
          onOpenChange={setOpen}
          tone="default"
          title="Finish these first"
          description="Your account can't be deleted yet. Once everything below is done, come back and try again."
          confirmLabel="Got it"
          onConfirm={() => setOpen(false)}
          testId="delete-account-blocked-modal"
        >
          <ol className="list-decimal space-y-3 pl-5 text-sm text-dash-ink">
            {blockers.map((b) => (
              <li key={b.code}>
                {b.message}
                {b.deals && (
                  <ul className="mt-1.5 list-disc space-y-1 pl-5 text-dash-muted">
                    {b.deals.map((d) => (
                      <li key={d.id}>
                        <Link to={`/deals/${d.id}`} className="underline hover:text-dash-greenDeep">{d.address}</Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </ConfirmDialog>
      ) : (
        <ConfirmDialog
          onOpenChange={setOpen}
          title="Delete your account?"
          description="This happens right away and can't be undone. We'll email you a confirmation."
          expectedText="DELETE"
          confirmationLabel="Type DELETE to confirm"
          confirmLabel="Delete account"
          busyLabel="Deleting…"
          onConfirm={submit}
          testId="delete-account-modal"
        >
          <div className="space-y-4 rounded-lg bg-dash-hover px-4 py-3">
            <DetailList
              heading="What gets removed"
              items={check.cancels_subscription
                ? [...DELETE_REMOVES, "Your Stoop subscription, cancelled right away with no further charges"]
                : DELETE_REMOVES}
            />
            <DetailList heading="What stays" items={DELETE_KEEPS} />
          </div>
        </ConfirmDialog>
      ))}
    </SectionCard>
  );
}

function ChangePasswordForm({ onBack }: { onBack: () => void }) {
  const { mutate: changePassword, isPending } = useChangePassword();
  const form = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: { currentPassword: "", password: "", confirmPassword: "" },
  });

  const updatePassword = (values: ChangePasswordValues) => {
    changePassword(
      { current_password: values.currentPassword, new_password: values.password },
      { onSuccess: () => { form.reset(); onBack(); } },
    );
  };

  return (
    <Form {...form}>
      <SectionCard>
        <Button variant="seamless"
          type="button"
          onClick={onBack}
          className="mb-4 flex items-center gap-1.5 text-xs font-medium text-dash-muted hover:text-dash-ink"
          data-testid="settings-back-to-security"
        >
          <ArrowLeft className="size-3.5" /> Back
        </Button>
        <h2 className="mb-1 text-sm font-semibold text-dash-ink">Change password</h2>
        <p className="mb-5 text-xs text-dash-faint">
          Your current password is required, and every other signed-in device will be signed out.
        </p>
        <form className="flex max-w-sm flex-col gap-5" onSubmit={form.handleSubmit(updatePassword)} noValidate>
          <FormInput name="currentPassword" label="Current password" type="password" autoComplete="current-password" placeholder="Enter current password" required />
          <FormInput name="password" label="Enter new password" type="password" autoComplete="new-password" placeholder="Enter password" required />
          <FormInput name="confirmPassword" label="Confirm new password" type="password" autoComplete="new-password" placeholder="Enter password" required />
          <Button
            type="submit"
            variant="dashboardPrimary"
            loading={isPending}
            data-testid="settings-submit-password"
          >
            Update password
          </Button>
        </form>
      </SectionCard>
    </Form>
  );
}

export default function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "profile";
  const [securityView, setSecurityView] = useState("list");
  const { context } = useAuth();
  const orgId = context?.organization?.id ?? "";
  const isExternal = Boolean(context && !context.membership);
  const tabs = isExternal ? SETTINGS_TABS.filter((item) => !item.orgOnly) : SETTINGS_TABS;
  // An external landing on a hidden tab (stale state, or a membership that went
  // away) would otherwise render nothing at all, since the panel below is keyed
  // off `tab` and every org-only branch is gated on `orgId`.
  const activeTab = tabs.some((item) => item.key === tab) ? tab : "profile";

  const onTabClick = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
    if (key === "security") setSecurityView("list");
  };

  return (
    <div className="font-dashboard" data-testid="settings-page">
      <PageHeader
        title="Settings"
        description={isExternal
          ? "Configure your profile and security."
          : "Configure your profile, reminders, calendar, brokerage, status templates, and security."}
      />

      <div className="mt-6 flex flex-col gap-6 sm:flex-row">
        <nav className="flex w-full shrink-0 flex-col gap-1 sm:w-56">
          {tabs.map((item) => (
            <Button variant="seamless"
              type="button"
              key={item.key}
              onClick={() => onTabClick(item.key)}
              className={cn(
                "w-full rounded-xl px-4 py-2.5 text-left text-sm font-medium transition-colors",
                activeTab === item.key ? "bg-dash-hover text-dash-ink font-semibold" : "text-dash-muted hover:bg-dash-hover/60 hover:text-dash-ink"
              )}
              aria-pressed={activeTab === item.key}
              data-testid={`settings-tab-${item.key}`}
            >
              {item.label}
            </Button>
          ))}
        </nav>

        <div className="min-w-0 flex-1">
          {activeTab === "profile" && <MyProfile />}
          {activeTab === "reminders" && <RemindersSettings />}
          {activeTab === "calendar" && <GoogleCalendarSettings />}
          {activeTab === "brokerage" && (
            orgId
              ? <BrokerageProfile orgId={orgId} />
              : <EmptyState title="No organization" description="Brokerage settings are available to organization members." />
          )}
          {activeTab === "status-templates" && (
            orgId
              ? <StatusTemplates orgId={orgId} />
              : <EmptyState title="No organization" description="Status templates are available to organization members." />
          )}
          {activeTab === "security" && (
            securityView === "list"
              ? <SecurityList onChangePassword={() => setSecurityView("password")} />
              : <ChangePasswordForm onBack={() => setSecurityView("list")} />
          )}
        </div>
      </div>
    </div>
  );
}
